import { useEffect, useRef, useState } from "react";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import { cn } from "../../../../utils/cn";
import { getFranchiseDisplayName } from "../../../../utils/help/localization/localization";
import { Franchise } from "../../../../utils/types";
import {
  CarPickupIcon,
  DineInIcon,
  PickupIcon,
} from "../../../shared/SelectOrderInfo/SelectOrderInfoV3Icons";
import { DeliveryTypeDisplayName } from "../../../../utils/enums";
import { SearchInput } from "../../../shared/SearchInput/SearchInput";
import { MapCtaButtons } from "../MapCtaFooter/MapCtaButtons";
import { MapDetectLocationIcon } from "../Map.icons";
import styles from "./MapScreenV3.module.scss";

type Props = {
  activeTabLabel: string;
  /**
   * Icon key of the order type being booked (see getFranchiseOrderTypeIcons).
   * Only this icon turns teal on the selected branch card; the rest stay grey.
   */
  activeOrderIconKey?: string;
  franchises: (Franchise & { distanceFromLocationInMeters?: number })[];
  selectedFranchiseId: number | undefined;
  onSelectFranchise: (id: number) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  isDetecting: boolean;
  onDetect: () => void;
  /** `detectNearestBranchLocation` — when false the FAB is not rendered. */
  canDetectLocation: boolean;
  onSkip: () => void;
  onProceed: () => void;
  selectedFranchise: Franchise | undefined;
};

function getFranchiseOrderTypeIcons(franchise: Franchise) {
  const config = franchise.franchiseConfig;
  const icons: { key: string; Icon: () => JSX.Element }[] = [];

  const deliveryTypeDisplayNames = new Set(
    (config?.deliveryV2 ?? []).map((c) => c.deliveryType.displayName)
  );

  // Only show car pickup icon — other delivery types are omitted from the branch card
  if (deliveryTypeDisplayNames.has(DeliveryTypeDisplayName.CAR_PICKUP)) {
    icons.push({ key: "car_pickup", Icon: CarPickupIcon });
  }

  if (config?.takeOut) {
    icons.push({ key: "takeout", Icon: PickupIcon });
  }
  if (config?.dineIn) {
    icons.push({ key: "dine_in", Icon: DineInIcon });
  }

  return icons;
}

function formatDistance(meters?: number): string | null {
  if (!meters) {
    return null;
  }

  return meters < 1000
    ? `${Math.round(meters)}m`
    : `${(meters / 1000).toFixed(1)}km`;
}

export const MapScreenV3BranchList = ({
  activeTabLabel,
  activeOrderIconKey,
  franchises,
  selectedFranchiseId,
  onSelectFranchise,
  searchQuery,
  onSearchChange,
  isDetecting,
  onDetect,
  canDetectLocation,
  onSkip,
  onProceed,
  selectedFranchise,
}: Props) => {
  const { t } = useTranslationI18();
  const [hasClicked, setHasClicked] = useState(false);

  // Footer shows a shadow only when the branch list still has content hidden
  // underneath the fixed footer (design: "no shadow when not a list, shadow
  // when there's a list scrolling"). The list's own scroll container varies —
  // here the whole window scrolls, not a dedicated overflow element — so rather
  // than track a specific scroller we compare the list's bottom edge against
  // the footer's top edge and recompute on any scroll/resize.
  const listRef = useRef<HTMLUListElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerScrolled, setFooterScrolled] = useState(false);

  useEffect(() => {
    const update = () => {
      const list = listRef.current;
      const footer = footerRef.current;
      if (!list || !footer) {
        return;
      }

      const listBottom = list.getBoundingClientRect().bottom;
      const footerTop = footer.getBoundingClientRect().top;
      // Content still extends under the footer → there's more to scroll.
      setFooterScrolled(listBottom > footerTop + 1);
    };

    update();
    // capture:true catches scroll events from whichever element actually
    // scrolls (the window here), since scroll events don't bubble.
    window.addEventListener("scroll", update, { passive: true, capture: true });
    window.addEventListener("resize", update);
    const resizeObserver = new ResizeObserver(update);
    if (listRef.current) {
      resizeObserver.observe(listRef.current);
    }

    return () => {
      window.removeEventListener("scroll", update, { capture: true });
      window.removeEventListener("resize", update);
      resizeObserver.disconnect();
    };
  }, [franchises.length]);

  return (
    <>
      {/* SEARCH */}
      <div className={styles.search_container}>
        <SearchInput
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t("common:search-here")}
          testId="v3-branch-search"
        />
      </div>

      {/* LIST HEADING */}
      <p className={styles.list_heading}>
        {activeTabLabel}&nbsp;{t("common:branches")}
      </p>

      {/* EMPTY SEARCH STATE — only when a search actually returned nothing, not
          when the tab simply has no branches yet. */}
      {searchQuery.trim() && franchises.length === 0 && (
        <p className={styles.no_branches} data-testid="v3-no-branches">
          {t("common:no-branches-by-this-name")}
        </p>
      )}

      {/* BRANCH LIST */}
      <ul className={styles.branch_list} ref={listRef}>
        {franchises.map((franchise) => {
          const distance = formatDistance(
            franchise.distanceFromLocationInMeters
          );

          return (
            <li
              key={franchise.id}
              className={cn(
                styles.branch_item,
                selectedFranchiseId === franchise.id &&
                  styles.branch_item_selected
              )}
              onClick={() => onSelectFranchise(franchise.id)}
              data-testid={`v3-branch-item-${franchise.slug}`}
            >
              <span className={styles.branch_name}>
                {getFranchiseDisplayName(franchise)}
              </span>
              <div className={styles.branch_right}>
                <div className={styles.branch_order_icons}>
                  {getFranchiseOrderTypeIcons(franchise).map(
                    ({ key, Icon }) => (
                      <span
                        key={key}
                        className={cn(
                          styles.branch_order_icon,
                          key === activeOrderIconKey &&
                            styles.branch_order_icon_active
                        )}
                      >
                        <Icon />
                      </span>
                    )
                  )}
                </div>
                {distance && (
                  <span className={styles.branch_distance}>{distance}</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* DETECT LOCATION FAB */}
      {canDetectLocation && (
        <button
          className={cn(
            styles.detect_fab,
            hasClicked && styles.detect_fab_collapsed,
            isDetecting && styles.detect_fab_loading
          )}
          onClick={() => {
            setHasClicked(true);
            onDetect();
          }}
          data-testid="v3-detect-location"
        >
          {/* Same icon component the delivery tab's detect button uses, so the
            two detect controls look identical across order types. */}
          <MapDetectLocationIcon size={24} />
          <span>{t("delivery:detect-your-location")}</span>
        </button>
      )}

      {/* FOOTER */}
      <div
        ref={footerRef}
        className={cn(styles.footer, footerScrolled && styles.footer_scrolled)}
      >
        <MapCtaButtons
          skipLabel={t("common:skip")}
          skipTestId="v3-location-skip"
          onSkip={onSkip}
          primaryLabel={t("common:proceed")}
          primaryTestId="v3-location-proceed"
          onPrimary={onProceed}
          primaryDisabled={!selectedFranchise}
        />
      </div>
    </>
  );
};
